import { useIsTablet } from '@hooks/responsive/useIsMobile';
import { useRouter } from 'next/router';

// components
import FloatingButtonSection from '@components/_molecules/FloatingButtonSection';
import Button from '@components/_atoms/Button';
import Typography from '@components/_atoms/Typography';

// constants
import { ButtonColor, TypoVariant } from '@constants/atoms';
import { red } from '@styles/Colors';
import { useCreateSaleSelfReRequestFetch } from '@hooks/fetch/useSaleSelfFetch';

// type
import { QuotationFormType } from '@components/_organism/service/QuotationFormCard';
import { useRef } from 'react';
import { useToggle } from '@hooks/toggle/useToggle';
import useHandleOutsideClickAction from '@hooks/interaction/useHandleOutsideClickAction';
import styled from '@emotion/styled';
import SubmitModal from '../../../../_organism/service/SubmitModal';
import { SELLER_REREQUEST_COMPLETE } from '@constants/routes/routes';
import { useTranslation } from 'next-i18next';
import ButtonCode from '@constants/log/buttonCode';

type Props = {
  rfq_number_id?: number;
  quotationForm: QuotationFormType;
  itemIds: number[];
};

const Layout = styled.div``;

export default function ButtonSection({ rfq_number_id, quotationForm, itemIds }: Props) {
  const { t } = useTranslation('seller');
  const isTablet = useIsTablet();
  const router = useRouter();

  const disabled =
    isTablet ||
    !quotationForm.req_close_days ||
    !quotationForm.req_delivery_days ||
    !quotationForm.shipping_area ||
    !quotationForm.terms_of_payment ||
    !itemIds.length;

  const isDoubleClick = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const openModal = useToggle();

  // 외부 클릭
  useHandleOutsideClickAction([modalRef, buttonRef], () => {
    openModal.off();
  });

  const { createSaleSelfReRequestMutate } = useCreateSaleSelfReRequestFetch();

  const handleFormSubmit = () => {
    if (isDoubleClick.current) return;
    isDoubleClick.current = true;

    const { req_close_days, req_delivery_days, shipping_area, terms_of_payment, additional_request_file, seller_note } =
      quotationForm;

    createSaleSelfReRequestMutate(
      {
        rfq_number_id,
        req_close_days: ~~(req_close_days ?? 0),
        req_delivery_days: ~~(req_delivery_days ?? 0),
        shipping_area,
        terms_of_payment,
        additional_request_file,
        seller_note,
        item_ids: itemIds,
      },
      {
        onSuccess: () => {
          router.replace(SELLER_REREQUEST_COMPLETE);
        },
      },
    );
  };

  return (
    <Layout>
      <FloatingButtonSection
        text={
          isTablet ? (
            <Typography variant={TypoVariant.B3} color={red.red3}>
              {t('detail.tablet-text')}
            </Typography>
          ) : null
        }
        button1={
          <Button
            color={ButtonColor.OutlinedBlack}
            text={t('detail.back-button')}
            onClick={() => router.back()}
            steelbosoId={ButtonCode.BTN_BACK}
          />
        }
        button2={
          <Button
            disabled={disabled}
            text={t('detail.submit-button')}
            onClick={handleFormSubmit}
            steelbosoId={ButtonCode.BTN_SUBMIT}
          />
        }
      />

      {/* Submit Modal */}
      <SubmitModal
        modalRef={modalRef}
        openModal={openModal.isToggled}
        handleFormSubmit={handleFormSubmit}
        handleModalClose={() => openModal.off()}
      />
      {/* Submit Modal end */}
    </Layout>
  );
}
